import { BASE_URL } from 'utils/baseUrlProvider';
import { extractSummaryText } from 'utils/aiSummaryHelper';

const maskWebSocketUrl = (url = '') =>
  url.replace(/(token|authorization)=([^&]+)/gi, '$1=***');

const appendQueryParam = (url, key, value) => {
  if (!value) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${key}=${encodeURIComponent(value)}`;
};

export const SUMMARY_WEBSOCKET_PATH = '/ws/patient-summary/';

export const buildWebSocketUrl = (path) => {
  const wsProtocol = /^https:/i.test(BASE_URL) ? 'wss:' : 'ws:';
  const host = BASE_URL.replace(/^https?:\/\//i, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  let url = `${wsProtocol}//${host}${normalizedPath}`;
  const token = localStorage.getItem('authToken');

  if (token) {
    url = appendQueryParam(url, 'token', token);
  }

  return url;
};

export const buildPatientSummaryStartMessage = (patientId, includeInterpretation = false) => ({
  action: 'generate_patient_summary',
  patient_id: patientId,
  include_interpretation: includeInterpretation
});

export const buildIPDSummaryStartMessage = (admissionId, includeInterpretation = false) => ({
  action: 'generate_ipd_summary',
  admission_id: admissionId,
  include_interpretation: includeInterpretation
});

export const parseAiStreamMessage = (raw) => {
  if (raw == null) return {};

  if (typeof raw !== 'string') {
    return { chunk: String(raw) };
  }

  const trimmed = raw.trim();
  if (!trimmed) return {};

  try {
    const payload = JSON.parse(trimmed);
    const eventType = payload.type || payload.event;

    if (eventType === 'error' || payload.success === false) {
      return {
        error:
          payload.message ||
          payload.error ||
          payload.detail ||
          (typeof payload.errors === 'string' ? payload.errors : null) ||
          'Stream error'
      };
    }

    if (eventType === 'status' || eventType === 'step' || eventType === 'progress') {
      return {
        status: payload.message || payload.status || payload.step || payload.phase || ''
      };
    }

    if (eventType === 'token') {
      const tokenContent =
        typeof payload.content === 'string'
          ? payload.content
          : typeof payload.text === 'string'
            ? payload.text
            : '';

      return tokenContent ? { chunk: tokenContent } : {};
    }

    if (
      eventType === 'final_payload' ||
      eventType === 'final' ||
      eventType === 'done' ||
      eventType === 'complete' ||
      eventType === 'end' ||
      eventType === 'summary_complete'
    ) {
      const fullText = extractSummaryText(payload);
      return {
        done: true,
        fullText: fullText || undefined,
        payload
      };
    }

    const chunk =
      payload.chunk ??
      payload.delta ??
      payload.content ??
      payload.text;

    if (typeof chunk === 'string' && chunk) {
      return { chunk };
    }

    const fullText = extractSummaryText(payload);
    if (fullText) {
      return { fullText };
    }

    return {};
  } catch {
    return { chunk: raw };
  }
};

const isAbortError = (error) =>
  error?.name === 'AbortError' || error?.message === 'Summary stream cancelled';

export const streamAiSummaryWebSocket = ({
  wsUrl,
  startMessage,
  onChunk,
  onStatus,
  onComplete,
  onFinalPayload,
  onError,
  onTransportFallback,
  signal,
  connectTimeoutMs = 8000,
  idleTimeoutMs = 180000
}) =>
  new Promise((resolve, reject) => {
    if (!wsUrl) {
      const error = new Error('No WebSocket URL configured');
      onError?.(error);
      reject(error);
      return;
    }

    let socket = null;
    let fullText = '';
    let settled = false;
    let connectTimeoutId = null;
    let idleTimeoutId = null;

    const clearStreamTimeouts = () => {
      if (connectTimeoutId) {
        clearTimeout(connectTimeoutId);
        connectTimeoutId = null;
      }
      if (idleTimeoutId) {
        clearTimeout(idleTimeoutId);
        idleTimeoutId = null;
      }
    };

    const settle = (text, error, payload) => {
      if (settled) return;
      settled = true;
      clearStreamTimeouts();
      signal?.removeEventListener('abort', handleAbort);
      socket?.close();

      if (error) {
        onError?.(error);
        reject(error);
        return;
      }

      if (payload) {
        onFinalPayload?.(payload);
      }

      onComplete?.(text, payload);
      resolve(text);
    };

    const applyChunk = (chunk, replace = false) => {
      if (!chunk) return;
      fullText = replace ? chunk : `${fullText}${chunk}`;
      onChunk?.(chunk, fullText);
    };

    const resetIdleTimeout = () => {
      if (idleTimeoutId) {
        clearTimeout(idleTimeoutId);
      }

      idleTimeoutId = setTimeout(() => {
        socket?.close();
        if (fullText) {
          settle(fullText);
        } else {
          const error = new Error('WebSocket summary stream timed out');
          onTransportFallback?.(error);
          settle(null, error);
        }
      }, idleTimeoutMs);
    };

    const handleAbort = () => {
      socket?.close();
      settle(null, new DOMException('Summary stream cancelled', 'AbortError'));
    };

    const failToFallback = (reason) => {
      if (process.env.NODE_ENV === 'development') {
        console.info('[ai-summary] WebSocket failed:', reason, maskWebSocketUrl(wsUrl));
      }

      const error = new Error('WebSocket summary stream unavailable');
      onTransportFallback?.(error);
      settle(null, error);
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }

    signal?.addEventListener('abort', handleAbort);

    if (process.env.NODE_ENV === 'development') {
      console.info('[ai-summary] Connecting WebSocket:', maskWebSocketUrl(wsUrl));
    }

    try {
      socket = new WebSocket(wsUrl);
    } catch (error) {
      failToFallback(error.message);
      return;
    }

    connectTimeoutId = setTimeout(() => {
      if (socket?.readyState !== WebSocket.OPEN) {
        socket?.close();
        failToFallback('connect-timeout');
      }
    }, connectTimeoutMs);

    socket.onopen = () => {
      clearStreamTimeouts();
      resetIdleTimeout();

      if (startMessage) {
        try {
          socket.send(JSON.stringify(startMessage));
        } catch (error) {
          failToFallback(error.message);
        }
      }
    };

    socket.onmessage = (event) => {
      resetIdleTimeout();
      const parsed = parseAiStreamMessage(event.data);

      if (parsed.error) {
        // Errors after the summary has streamed (e.g. citation verification)
        // keep the streamed text instead of regenerating it over HTTP.
        if (fullText) {
          settle(fullText);
          return;
        }
        settle(null, new Error(parsed.error));
        return;
      }

      if (parsed.status) {
        onStatus?.(parsed.status);
      }

      if (parsed.payload) {
        onFinalPayload?.(parsed.payload);
      }

      if (parsed.fullText) {
        applyChunk(parsed.fullText, true);
      } else if (parsed.chunk) {
        applyChunk(parsed.chunk);
      }

      if (parsed.done) {
        if (!fullText && parsed.payload) {
          const payloadText = extractSummaryText(parsed.payload);
          if (payloadText) {
            applyChunk(payloadText, true);
          }
        }

        if (!fullText) {
          settle(null, new Error('Summary stream completed without content'), parsed.payload);
          return;
        }

        settle(fullText, null, parsed.payload);
        return;
      }
    };

    socket.onerror = () => {
      if (process.env.NODE_ENV === 'development') {
        console.info('[ai-summary] WebSocket error on:', maskWebSocketUrl(wsUrl));
      }
    };

    socket.onclose = (event) => {
      clearStreamTimeouts();
      if (settled) return;

      if (fullText) {
        settle(fullText);
        return;
      }

      failToFallback(`closed-${event.code}`);
    };
  }).catch((error) => {
    if (isAbortError(error)) {
      throw error;
    }

    throw error;
  });
